import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const RentalYieldCalculator = () => {
  // State for basic inputs
  const [propertyValue, setPropertyValue] = useState(5000000);
  const [monthlyRent, setMonthlyRent] = useState(25000);
  const [occupancyRate, setOccupancyRate] = useState(95);
  
  // State for expenses
  const [maintenanceCost, setMaintenanceCost] = useState(10000);
  const [propertyTax, setPropertyTax] = useState(15000);
  
  // State for advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [appreciation, setAppreciation] = useState(5);
  const [loanAmount, setLoanAmount] = useState(0);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTerm, setLoanTerm] = useState(20);
  
  // State for share toast
  const [showShareToast, setShowShareToast] = useState(false);
  
  // State for tooltips
  const [showToolTip, setShowToolTip] = useState('');
  
  // State for calculation results
  const [result, setResult] = useState({
    grossYield: 0,
    netYield: 0,
    annualRent: 0,
    annualExpenses: 0,
    netRentalIncome: 0,
    capitalAppreciation: 0,
    totalReturn: 0,
    emi: 0,
    cashOnCash: 0
  });

  // Calculate rental yield
  const calculateRentalYield = () => {
    // Calculate annual rent with occupancy rate
    const annualRent = monthlyRent * 12 * (occupancyRate / 100);
    
    // Calculate annual expenses
    const annualExpenses = maintenanceCost + propertyTax;
    
    // Calculate net rental income
    const netRentalIncome = annualRent - annualExpenses;
    
    // Calculate gross yield
    const grossYield = (annualRent / propertyValue) * 100;
    
    // Calculate net yield
    const netYield = (netRentalIncome / propertyValue) * 100;
    
    // Calculate capital appreciation (annual)
    const capitalAppreciation = (propertyValue * appreciation) / 100;
    
    // Calculate total return (yield + appreciation)
    const totalReturn = netYield + appreciation;
    
    // Calculate EMI if loan is taken
    let emi = 0;
    if (loanAmount > 0) {
      const monthlyInterestRate = interestRate / 12 / 100;
      const numberOfPayments = loanTerm * 12;
      
      emi = loanAmount * monthlyInterestRate * 
        Math.pow(1 + monthlyInterestRate, numberOfPayments) / 
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
      
      // Annualize EMI
      emi = emi * 12;
    }
    
    // Calculate cash-on-cash return if loan is taken
    let cashOnCash = 0;
    if (loanAmount > 0) {
      const equity = propertyValue - loanAmount;
      const cashFlow = netRentalIncome - emi;
      
      cashOnCash = (cashFlow / equity) * 100;
    }
    
    return {
      grossYield,
      netYield,
      annualRent,
      annualExpenses,
      netRentalIncome,
      capitalAppreciation,
      totalReturn,
      emi,
      cashOnCash
    };
  };

  // Recalculate whenever inputs change
  useEffect(() => {
    setResult(calculateRentalYield());
  }, [
    propertyValue, 
    monthlyRent, 
    occupancyRate,
    maintenanceCost,
    propertyTax,
    appreciation,
    loanAmount,
    interestRate,
    loanTerm
  ]);

  // Handle share button click
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Rental Yield Calculator</h1>
        <h2 className="text-lg text-gray-600">Analyze the profitability of your rental property investment</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Property Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Property Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Property Value
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('propertyValue')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'propertyValue' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Current market value of the property
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={propertyValue}
                    onChange={(e) => setPropertyValue(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={500000}
                    max={20000000}
                    step={100000}
                    value={propertyValue}
                    onChange={(e) => setPropertyValue(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹5L</span>
                    <span>₹2Cr</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Rental Income */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Rental Income</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5">Monthly Rent</label>
                  <input
                    type="number"
                    value={monthlyRent}
                    onChange={(e) => setMonthlyRent(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={5000}
                    max={200000}
                    step={1000}
                    value={monthlyRent}
                    onChange={(e) => setMonthlyRent(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹5K</span>
                    <span>₹2L</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Occupancy Rate (%)
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('occupancy')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'occupancy' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Percentage of time the property is occupied in a year
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={occupancyRate}
                    onChange={(e) => setOccupancyRate(Math.min(100, Math.max(0, Number(e.target.value))))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={occupancyRate}
                    onChange={(e) => setOccupancyRate(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Annual Expenses</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Maintenance Cost
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('maintenance')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'maintenance' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Annual cost for repairs, maintenance, and society charges
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={maintenanceCost}
                    onChange={(e) => setMaintenanceCost(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={200000}
                    step={1000}
                    value={maintenanceCost}
                    onChange={(e) => setMaintenanceCost(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹0</span>
                    <span>₹2L</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Property Tax</label>
                  <input
                    type="number"
                    value={propertyTax}
                    onChange={(e) => setPropertyTax(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={100000}
                    step={1000}
                    value={propertyTax}
                    onChange={(e) => setPropertyTax(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹0</span>
                    <span>₹1L</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Annual Appreciation (%)
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('appreciation')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'appreciation' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Expected annual increase in property value
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={appreciation}
                        onChange={(e) => setAppreciation(Number(e.target.value))}
                        step="0.1"
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={20}
                        step={0.1}
                        value={appreciation}
                        onChange={(e) => setAppreciation(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>20%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">Loan Amount</label>
                      <input
                        type="number"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(Math.min(propertyValue, Math.max(0, Number(e.target.value))))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={propertyValue}
                        step={100000}
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>₹0</span>
                        <span>{formatCurrency(propertyValue)}</span>
                      </div>
                    </div>
                    
                    {loanAmount > 0 && (
                      <>
                        <div>
                          <label className="block font-medium text-sm mb-1.5">Interest Rate (%)</label>
                          <input
                            type="number"
                            value={interestRate}
                            onChange={(e) => setInterestRate(Number(e.target.value))}
                            step="0.1"
                            className="w-full p-1.5 border rounded text-sm"
                          />
                          <input
                            type="range"
                            min={5}
                            max={15}
                            step={0.1}
                            value={interestRate}
                            onChange={(e) => setInterestRate(Number(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>5%</span>
                            <span>15%</span>
                          </div>
                        </div>

                        <div>
                          <label className="block font-medium text-sm mb-1.5">Loan Term (years)</label>
                          <input
                            type="number"
                            value={loanTerm}
                            onChange={(e) => setLoanTerm(Number(e.target.value))}
                            className="w-full p-1.5 border rounded text-sm"
                          />
                          <input
                            type="range"
                            min={5}
                            max={30}
                            step={1}
                            value={loanTerm}
                            onChange={(e) => setLoanTerm(Number(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>5 years</span>
                            <span>30 years</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Rental Yield Analysis</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {result.netYield.toFixed(2)}%
                  </div>
                  <div className="text-sm text-gray-300">Net Rental Yield</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Gross Rental Yield</span>
                    <span className="font-bold">{result.grossYield.toFixed(2)}%</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Annual Rental Income</span>
                    <span className="font-bold">{formatCurrency(result.annualRent)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Annual Expenses</span>
                    <span className="font-bold">{formatCurrency(result.annualExpenses)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Net Rental Income</span>
                    <span className="font-bold">{formatCurrency(result.netRentalIncome)}</span>
                  </div>

                  {loanAmount > 0 && (
                    <div className="flex justify-between items-center py-2 border-t border-white/20">
                      <span className="text-sm">Annual Loan Payment</span>
                      <span className="font-bold">{formatCurrency(result.emi)}</span>
                    </div>
                  )}

                  {appreciation > 0 && (
                    <div className="flex justify-between items-center py-2 border-t border-white/20">
                      <span className="text-sm">Est. Annual Appreciation</span>
                      <span className="font-bold">{formatCurrency(result.capitalAppreciation)}</span>
                    </div>
                  )}

                  {loanAmount > 0 && (
                    <div className="flex justify-between items-center py-2 border-t border-white/20">
                      <span className="text-sm">Cash-on-Cash Return</span>
                      <span className="font-bold">{result.cashOnCash.toFixed(2)}%</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Total Return (Yield + Appreciation)</span>
                    <span className="font-bold">{result.totalReturn.toFixed(2)}%</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Consult Real Estate Expert →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentalYieldCalculator;